import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitToken } from '@homeplay/app';
import { apiGetBettingOptions } from '@homeplay/api';
import { useCallOnceWhen } from '../../hooks/useCallOnceWhen';
import { useToggleSet } from '../../hooks/useToggleSet';
import { useCountdownTimer } from '../../hooks/useCountdownTimer';
import { formatOdds, isDirectClickMarket } from '../../utils/common';
import { buildSelectedOutcomeData } from '../../utils/ticketHelpers';
import WidgetError from '../WidgetError';
import RefreshButton from '../RefreshButton';
import classes from './BettingOptions.module.scss';

const BettingOptions = ({
  intentArgs,
  message,
  onOutcomeSelect,
  onLotteryViewed,
  onWidgetDataLoaded,
}) => {
  const unitToken = useSelector(selectUnitToken);
  const prefetchedData = intentArgs?.data;
  const [ignorePrefetchedData, setIgnorePrefetchedData] = useState(false);
  const lotteryId = intentArgs?.lottery_id;

  const [data, setData] = useState(prefetchedData ?? null);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!unitToken || !lotteryId) return;
    setIsFetching(true);
    setError(null);
    try {
      const result = await apiGetBettingOptions({ unitToken, lotteryId });
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setIsFetching(false);
    }
  }, [unitToken, lotteryId]);

  useEffect(() => {
    if (prefetchedData && !ignorePrefetchedData) return;
    fetchData();
  }, [fetchData, prefetchedData, ignorePrefetchedData]);

  const lotteryData = data;
  const isDataLoaded = !!lotteryData || !!error;
  useCallOnceWhen(isDataLoaded, onWidgetDataLoaded);

  useEffect(() => {
    if (lotteryId && onLotteryViewed) onLotteryViewed(lotteryId);
  }, [lotteryId, onLotteryViewed]);

  const { countdown, isExpired: isBetsClosed } = useCountdownTimer({
    targetDate: lotteryData?.next_draw?.draw_date,
    status: lotteryData?.next_draw?.status,
  });

  const expandedCategories = useToggleSet();
  const expandedMarkets = useToggleSet();

  const handleOutcomeClick = (category, market, outcome) => {
    if (!lotteryData) return;
    const lotteryMetadata = {
      lottery_id: lotteryData.lottery_id,
      lottery_name: lotteryData.lottery_name,
      main_grid_total: lotteryData.main_grid_total,
      secondary_grid_total: lotteryData.secondary_grid_total,
      secondary_unique: lotteryData.secondary_unique,
      next_draw: lotteryData.next_draw,
    };
    onOutcomeSelect?.(
      buildSelectedOutcomeData(
        lotteryMetadata,
        category.key,
        category.name,
        market.market_type,
        market.title,
        market.main_grid_numbers,
        market.secondary_grid_numbers,
        outcome
      )
    );
  };

  const handleRefresh = () => {
    if (prefetchedData && !ignorePrefetchedData) {
      setIgnorePrefetchedData(true);
    } else {
      fetchData();
    }
  };

  if (!lotteryData) {
    if (error) return <WidgetError onRetry={fetchData} error={error} />;
    return null;
  }

  return (
    <>
      {message && <p className={classes.IntroMessage}>{message}</p>}

      <div className={classes.Container + (isBetsClosed ? ' ' + classes.Expired : '')}>
        {isBetsClosed && (
          <div className={classes.RefreshSlot}>
            <RefreshButton onClick={handleRefresh} isFetching={isFetching} ariaLabel="Refresh options" />
          </div>
        )}

        <div className={isBetsClosed ? classes.ContentExpired : ''}>
          <div className={classes.Header}>
            <div className={classes.HeaderRow}>
              <h3 className={classes.HeaderTitle}>{lotteryData.lottery_name}</h3>
              {countdown && <span className={classes.HeaderCountdown}>Next draw: {countdown}</span>}
            </div>
            <p className={classes.HeaderSubtitle}>Select a category to view available markets</p>
          </div>

          {(lotteryData.categories ?? []).map((category) => {
            const isCategoryExpanded = expandedCategories.has(category.key);
            return (
              <div key={category.key} className={classes.CategoryItem}>
                <button
                  type="button"
                  onClick={() => expandedCategories.toggle(category.key)}
                  className={classes.CategoryHeader}
                  aria-expanded={isCategoryExpanded}
                >
                  <span className={classes.CategoryName}>{category.name}</span>
                  <span className={classes.ToggleIcon}>{isCategoryExpanded ? '−' : '+'}</span>
                </button>

                {isCategoryExpanded && (
                  <div className={classes.CategoryContent}>
                    {category.markets.map((market) => {
                      const marketKey = `${category.key}-${market.market_type}`;
                      const isMarketExpanded = expandedMarkets.has(marketKey);
                      const isDirect = isDirectClickMarket(market);

                      if (isDirect && market.outcomes?.length > 0) {
                        const firstOutcome = market.outcomes[0];
                        return (
                          <div key={market.market_type} className={classes.MarketItem}>
                            <button
                              type="button"
                              onClick={() => handleOutcomeClick(category, market, firstOutcome)}
                              className={classes.MarketHeader}
                            >
                              <span className={classes.MarketTitle}>{market.title}</span>
                              <span className={classes.OutcomeOdds}>{formatOdds(firstOutcome.odds)}</span>
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div key={market.market_type} className={classes.MarketItem}>
                          <button
                            type="button"
                            onClick={() => expandedMarkets.toggle(marketKey)}
                            className={classes.MarketHeader}
                            aria-expanded={isMarketExpanded}
                          >
                            <span className={classes.MarketTitle}>{market.title}</span>
                            <span className={classes.ToggleIcon}>{isMarketExpanded ? '−' : '+'}</span>
                          </button>

                          {isMarketExpanded && (
                            <div className={classes.MarketContent}>
                              {market.outcomes.map((outcome) => (
                                <button
                                  key={outcome.id}
                                  type="button"
                                  onClick={() => handleOutcomeClick(category, market, outcome)}
                                  className={classes.OutcomeItem}
                                >
                                  <span className={classes.OutcomeType}>{outcome.outcome_title}</span>
                                  <span className={classes.OutcomeOdds}>{formatOdds(outcome.odds)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isBetsClosed && (
          <div className={classes.ExpiredOverlay}>
            <span className={classes.ExpiredWatermark}>Closed</span>
          </div>
        )}
      </div>
    </>
  );
};

export default BettingOptions;
